import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '@mui/material';

import Button from '../../../components/Button';
import Drawer from '../../../components/Drawer';
import List from '../../../components/List';
import ListItem from '../../../components/ListItem';
import Typography from '../../../components/Typography';
import { i18nNS } from '../../../i18n';
import { format } from '../../../utils/datetime';
import { forMobile } from '../../../utils/media-queries';
import Skeleton from './Skeleton';
import { FeatureSummaryCard } from './styles';
import useFeatureUpdatesDrawerVM, { Props as VMProps } from './vm';

const FEATURE_ID = (identifier: string) => `feature-${identifier}`;
const FEATURE_TITLE_LABEL_ID = (identifier: string) => `feature-title-${identifier}`;

interface Props extends VMProps {}

const FeatureUpdatesDrawer = ({ open, onClose: handleClose }: Props) => {
  const { t } = useTranslation([i18nNS.SETTINGS, i18nNS.GLOSSARY, i18nNS.COMMON]);
  const isMobile = useMediaQuery(forMobile);

  const {
    isFetchingFeatureUpdates,
    featureUpdates,
    handleFeatureSeen: _handleFeatureSeen,
  } = useFeatureUpdatesDrawerVM({ open, onClose: handleClose });

  const handleFeatureSeen = (featureName: string) => () => {
    return _handleFeatureSeen(featureName);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      header={t('all_feature_updates', { ns: i18nNS.SETTINGS })}
      footer={
        !isMobile && (
          <Button
            variant="text"
            onClick={handleClose}
            aria-label={t('click_to_close_drawer', { ns: i18nNS.COMMON })}
          >
            {t('close', { ns: i18nNS.GLOSSARY })}
          </Button>
        )
      }
    >
      {isFetchingFeatureUpdates ? (
        <Skeleton />
      ) : featureUpdates.length === 0 ? (
        <Typography color="grey.800">
          {t('there_are_no_feature_updates_to_be_displayed', { ns: i18nNS.SETTINGS })}
        </Typography>
      ) : (
        <List
          type="accessible"
          numItems={featureUpdates.length}
          getActiveItemId={(focusIndex) =>
            featureUpdates[focusIndex] ? FEATURE_ID(featureUpdates[focusIndex].featureName) : undefined
          }
          sx={{ gap: 6 }}
        >
          {(focusIndex) =>
            featureUpdates.map(
              (
                { featureName, releasedOn, isSeen, title, message, isBlog, viewMoreLabel, redirectUrl },
                index
              ) => (
                <ListItem
                  key={featureName}
                  id={FEATURE_ID(featureName)}
                  isFocused={focusIndex === index}
                  component={FeatureSummaryCard}
                  aria-labelledby={FEATURE_TITLE_LABEL_ID(featureName)}
                >
                  <Typography variant="paragraphRegular" color="grey.400">
                    {format(releasedOn, 'do MMM, yyyy')}
                  </Typography>
                  <Typography
                    id={FEATURE_TITLE_LABEL_ID(featureName)}
                    variant="h6Medium"
                    color={isSeen ? 'grey.400' : 'primary.500'}
                    component="div"
                  >
                    {title}
                  </Typography>
                  <Typography variant="paragraphRegular">
                    {message.split('\n').map((line, index) => (
                      <Fragment key={index}>
                        {line}
                        <br />
                      </Fragment>
                    ))}
                  </Typography>
                  {isBlog && Boolean(viewMoreLabel) && (
                    <Button
                      component="a"
                      href={redirectUrl}
                      target="_blank"
                      onClick={handleFeatureSeen(featureName)}
                      variant="outlined"
                    >
                      {viewMoreLabel}
                    </Button>
                  )}
                </ListItem>
              )
            )
          }
        </List>
      )}
    </Drawer>
  );
};

export default FeatureUpdatesDrawer;
